import { useEffect, useState, useCallback } from "react";
import { Radio } from "lucide-react";
import api from "../lib/apiClient";

function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const counts = points.map((p) => p.count);
  const max = Math.max(1, ...counts);
  const w = 180, h = 44, n = points.length;
  const stepX = w / (n - 1);
  const coords = counts.map((c, i) => [i * stepX, h - (c / max) * (h - 6) - 3]);
  const line = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${w},${h} L0,${h} Z`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(16 185 129)" stopOpacity="0.28" />
          <stop offset="100%" stopColor="rgb(16 185 129)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#spark-fill)" />
      <path d={line} fill="none" stroke="rgb(16 185 129)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {coords.length > 0 && (
        <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="2.6" fill="rgb(16 185 129)" />
      )}
    </svg>
  );
}

const RANGES = [
  { id: "60m", label: "60m", caption: "Last 60 min" },
  { id: "24h", label: "24h", caption: "Last 24 hours" },
];

// Live count + visitor trend sparkline (60-minute or 24-hour view) for the admin Overview.
export default function LiveVisitors() {
  const [active, setActive] = useState(null);
  const [points, setPoints] = useState([]);
  const [range, setRange] = useState("60m");
  const [error, setError] = useState(false);

  const load = useCallback(async (win) => {
    try {
      const [p, t] = await Promise.all([
        api.get("/admin/presence"),
        api.get("/admin/presence/trend", { params: { window: win } }),
      ]);
      setActive(p.data.active);
      setPoints(t.data.points || []);
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    load(range);
    const id = setInterval(() => load(range), 15000);
    return () => clearInterval(id);
  }, [load, range]);

  const peak = points.length ? Math.max(...points.map((p) => p.count)) : 0;
  const peakPt = points.find((p) => p.count === peak);
  const caption = RANGES.find((r) => r.id === range)?.caption || "";

  return (
    <div className="card-soft rounded-2xl p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="live-visitors">
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-emerald-500/15 text-emerald-600 shrink-0">
          <Radio className="w-5 h-5" />
        </div>
        <div>
          <span className="eyebrow flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            Live now
          </span>
          <p className="font-display font-extrabold text-3xl mt-1" data-testid="live-visitors-count">
            {error ? "—" : active === null ? "…" : active}
          </p>
          <p className="text-xs text-muted-foreground -mt-0.5">
            {active === 1 ? "visitor on the site right now" : "visitors on the site right now"}
          </p>
        </div>
      </div>

      <div className="flex flex-col items-start sm:items-end gap-1">
        <div className="inline-flex rounded-lg border border-border overflow-hidden" data-testid="live-visitors-range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              data-testid={`live-visitors-range-${r.id}`}
              className={`px-3 py-1 text-xs font-semibold transition-colors ${range === r.id ? "bg-emerald-500 text-white" : "bg-transparent text-muted-foreground hover:bg-secondary"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div data-testid="live-visitors-sparkline"><Sparkline points={points} /></div>
        <p className="text-[11px] text-muted-foreground" data-testid="live-visitors-caption">{caption} · peak {peak}{peakPt ? ` @ ${peakPt.t}` : ""}</p>
      </div>
    </div>
  );
}
